import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '../../../../components/hooks/useTheme';
import useAuth from '../../../../components/hooks/useAuth';
import useConfirm from '../../../../components/hooks/useConfirm';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Button, Dialog, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import axios from 'axios';
import baseURL from '../../../../configs/api';
import PleaseWaitPopup from '../../../PleaseWaitPopup';
import colorConfigs from '../../../../configs/colorConfigs';
import CustomFormatDatePicker from '../../../../components/common/CustomFormatDatePicker';
import { validateMin } from '../../../../utils/validateMin';
import AddIcon from '@mui/icons-material/Add';
import LocalSeeIcon from '@mui/icons-material/LocalSee';
import Webcam from "react-webcam";
import { delete_fileUpload, post_fileUpload } from '../../../../services/appFileUpload';
import { handleCapture } from '../../../../utils/covertTheBase64FormatIntoImage';
import { CustomImagePopup } from '../../../../components/common/CustomImagePopup';
import useGeneral from '../../../../components/hooks/useGeneral';
import Cookies from 'js-cookie';
import useTenant from '../../../../components/hooks/useTenant';

type AddExpenseProps = {
    idNumber: any;
    openAddExpense: boolean;
    setOpenAddExpense: (value: boolean) => void;
    setRefreshExpenseList: (value: boolean) => void;
}

export const AddExpense = ({ idNumber, openAddExpense, setOpenAddExpense, setRefreshExpenseList }: AddExpenseProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { dateFormat }: any = useGeneral();
    const [expenseName, setExpenseName] = useState<string>("");
    const [amount, setAmount] = useState<number>(0);
    const [vehicleExpenseTypeId, setVehicleExpenseTypeId] = useState<string>("");
    const [expenseTypeOptions, setExpenseTypeOptions] = useState<any[]>([]);
    const [expenseDate, setExpenseDate] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [isRecurringExpense, setIsRecurringExpense] = useState<boolean>(false);
    const [pleaseWait, setPleaseWait] = useState<boolean>(false);

    const [openAddTypeDialog, setOpenAddTypeDialog] = useState(false);
    const [newExpenseTypeName, setNewExpenseTypeName] = useState('');
    const [isActiveType, setIsActiveType] = useState(true);

    const inputFileRef = useRef<HTMLInputElement>(null);
    const [filePath, setFilePath] = useState<any[]>([]);
    const [fileIndex, setFileIndex] = useState<number>(-1);
    const [imagePopup, setImagePopup] = useState<boolean>(false);
    const [showWebcam, setShowWebcam] = useState<boolean>(false);
    const webRef = useRef(null);

    const fetchExpenseTypes = () => {
        axios.get(`${baseURL}vehicleExpenseType/GetVehicleExpenseTypesWithActiveStatus/true`, {
            headers: {
                'X-Tenant-ID': tenantId,
            }
        })
            .then((response) => {
                const activeTypes = response?.data?.filter((item: any) => item?.isActive);
                setExpenseTypeOptions(
                    activeTypes?.map((item: any) => ({
                        value: item.id,
                        label: item.expenseTypeName
                    }))
                );

                if (activeTypes?.length > 0) {
                    setVehicleExpenseTypeId(activeTypes[0]?.id);
                } else {
                    setVehicleExpenseTypeId("0");
                }
                handleTenantError(null);
            })
            .catch((error) => {
                setExpenseTypeOptions([]);
                setVehicleExpenseTypeId("0");
                handleTenantError(error);
            });
    };

    useEffect(() => {
        fetchExpenseTypes();
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPleaseWait(true);
        const selectedFiles = e.target.files as FileList;

        if (selectedFiles.length !== 0) {
            const formData = new FormData();
            formData.append("ImageFile", selectedFiles[0]);

            axios
                .post(`${baseURL}commonFile/upload`, formData, {
                    headers: {
                        'X-Tenant-ID': tenantId,
                        "Content-Type": "multipart/form-data"
                    },
                    withCredentials: true,
                })
                .then((response) => {
                    const newFilePath: string =
                        `${baseURL}commonFile/Get?name=` +
                        response.data;
                    setFilePath((prevPath: string[]) => [...prevPath, newFilePath]);
                    setPleaseWait(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                });
        } else {
            setPleaseWait(false);
        }
    };

    const handelSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!pleaseWait) {
            setPleaseWait(true);
            axios
                .post(
                    `${baseURL}vehicleExpense?userId=${auth?.id}`,
                    JSON.stringify({
                        vehicleId: idNumber?.state,
                        expenseName,
                        vehicleExpenseTypeId,
                        amount,
                        expenseDate,
                        description,
                        isRecurringExpense,
                        createdAt: new Date().toLocaleString(),
                        createdBy: auth?.givenname,
                    }),
                    {
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                        withCredentials: true,
                    }
                )
                .then((response) => {
                    saveImages(response?.data);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        }
    };

    const saveImages = (expenseId: number) => {
        if (filePath?.length !== 0) {
            filePath?.forEach(async (item: string, index: number) => {
                await post_fileUpload({
                    attributeName: "Expense",
                    attributeId: expenseId,
                    fileName: item?.replace(
                        `${baseURL}commonFile/Get?name=`,
                        ""
                    ),
                    tabName: "Expense",
                    userId: auth?.id ?? 0,
                    createdDate: new Date(),
                }, auth?.id, tenantId)
                    .then(() => {
                        if (filePath?.length - 1 === index) {
                            setPleaseWait(false);
                            setOpenAddExpense(false);
                            setRefreshExpenseList(true);
                        }
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setPleaseWait(false);
                    });
            });
        } else {
            setPleaseWait(false);
            setOpenAddExpense(false);
            setRefreshExpenseList(true);
        }
    }

    return (
        <>
            <Dialog
                open={openAddExpense}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '550px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[10px] py-[5px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>

                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Expense</h2>

                                    <span className={`material-symbols-outlined text-[25px] font-light cursor-pointer ${themeStyles.main.textHeadingColor}`} onClick={() => setOpenAddExpense(false)}>close</span>

                                </div>
                                <form onSubmit={handelSubmit}>
                                    <div className="grid grid-cols-2 gap-6 mt-4">
                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Expense Name</label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={expenseName}
                                                    onChange={(e) => {
                                                        setExpenseName(e.target.value)
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Amount</label>
                                                <input
                                                    type="number"
                                                    step={0.01}
                                                    min={0}
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={amount}
                                                    onChange={(e) => {
                                                        setAmount(validateMin(e.target.value, 0))
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-full mt-4">
                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Expense Type</label>
                                        <select
                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            value={vehicleExpenseTypeId}
                                            onChange={(e) => setVehicleExpenseTypeId(e.target.value)}
                                            required
                                        >
                                            {
                                                expenseTypeOptions?.map((item: any, index: number) => {
                                                    return <option className={`${themeStyles.main.tailwindSectionBg}`} key={index} value={item.value}>{item.label}</option>;
                                                })
                                            }
                                        </select>
                                    </div>
                                    <Button
                                        onClick={() => setOpenAddTypeDialog(true)}
                                        sx={{
                                            fontSize: '12px',
                                            marginTop: '6px',
                                            padding: 0,
                                            textTransform: 'none',
                                            minWidth: 'auto'
                                        }}
                                    >
                                        + Add Expense Type
                                    </Button>

                                    <div className="grid grid-cols-2 gap-6 mt-4">
                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Date</label>
                                                <CustomFormatDatePicker
                                                    value={expenseDate}
                                                    onChange={setExpenseDate}
                                                    dateFormat={dateFormat}
                                                    required={true}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Recurring Expense</label>
                                            <RadioGroup
                                                row
                                                value={isRecurringExpense ? "Yes" : "No"}
                                                onChange={(e) => setIsRecurringExpense(e.target.value === "Yes" ? true : false)}
                                            >
                                                <FormControlLabel
                                                    value="Yes"
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                color: "gray",
                                                                "&.Mui-checked": {
                                                                    color: "#167AC3",
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: 14,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label="Yes"
                                                    sx={{
                                                        color: themeStyles.form.labelTextColor
                                                    }}
                                                />

                                                <FormControlLabel
                                                    value="No"
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                color: "gray",
                                                                "&.Mui-checked": {
                                                                    color: "#167AC3",
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: 14,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label="No"
                                                    sx={{
                                                        color: themeStyles.form.labelTextColor
                                                    }}
                                                />
                                            </RadioGroup>
                                        </div>
                                    </div>

                                    <div className="w-full mt-4">
                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Description</label>
                                        <textarea
                                            className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            rows={6}
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                    </div>

                                    <div className="w-full mt-4">
                                        <div className="w-full flex flex-row items-center gap-[5px] flex-wrap">
                                            {
                                                filePath?.map((item: any, outerIndex: number) => (
                                                    <React.Fragment key={outerIndex}>
                                                        <img
                                                            src={item}
                                                            width={40}
                                                            height={40}
                                                            alt="expense"
                                                            onClick={() => {
                                                                setFileIndex(outerIndex);
                                                                setImagePopup(true);
                                                            }}
                                                        />
                                                        <RemoveCircleIcon
                                                            className={`text-[14px] ${themeStyles.main.textSubHeadingColor} font-bold  hover:text-red-500 cursor-pointer`}
                                                            onClick={() => {

                                                                setConfirmData({
                                                                    title: 'Delete image',
                                                                    summary: 'Are you sure you want to delete this image?',
                                                                    confirmBtn: 'Yes',
                                                                    isConfirm: (callback: any) => {
                                                                        let newFileSet: string[] = filePath.filter(
                                                                            (_, index: number) => index !== outerIndex
                                                                        );
                                                                        if (filePath[outerIndex].id === 0) {
                                                                            setFilePath(() => newFileSet);
                                                                        } else {
                                                                            delete_fileUpload(filePath[outerIndex]?.id, auth?.id, tenantId)
                                                                                .then(() => {
                                                                                    setFilePath(() => newFileSet);
                                                                                    handleTenantError(null);
                                                                                })
                                                                                .catch((error) => {
                                                                                    handleTenantError(error);
                                                                                });
                                                                        }
                                                                        callback();
                                                                    }
                                                                });

                                                                setConfirmPopup(true);
                                                            }}

                                                        />
                                                    </React.Fragment>
                                                ))
                                            }
                                        </div>
                                        <div>
                                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Attachments</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={inputFileRef}
                                                onChangeCapture={onFileChangeCapture}
                                                style={{ display: "none" }}
                                            />
                                            <div className="flex flex-row gap-2 mt-2">
                                                <p
                                                    className="border border-dashed border-black rounded px-2 py-[3px] flex justify-center items-center text-[12px] bg-white cursor-pointer"
                                                    onClick={() => {

                                                        if (inputFileRef.current !== null) {
                                                            inputFileRef.current.value = "";

                                                        }
                                                        inputFileRef.current?.click();
                                                    }}
                                                >
                                                    <AddIcon className="text-[#167AC3] text-[14px] font-bold" />
                                                    Add Image
                                                </p>

                                                <p
                                                    className="border border-dashed border-black rounded px-2 py-[3px] flex justify-center items-center text-[12px] bg-white cursor-pointer"
                                                    onClick={() => {

                                                        setShowWebcam(true)
                                                    }}
                                                >
                                                    <LocalSeeIcon className="text-[#167AC3] text-[14px] font-bold" />
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 w-full flex gap-4">
                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.main.bg
                                            }}
                                            type="submit"
                                        >
                                            Save
                                        </Button>

                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.cancel.bg,
                                                border: colorConfigs.btn.cancel.border,
                                                color: colorConfigs.btn.cancel.color
                                            }}
                                            onClick={() => setOpenAddExpense(false)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={openAddTypeDialog}
                maxWidth="xs"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        padding: '20px',
                    }
                }}
            >
                <div className={`px-[10px] py-[5px] ${themeStyles.main.tailwindSectionBg}`}>
                    <div className="flex justify-between items-center border-b pb-2">
                        <h2 className={`text-base font-semibold ${themeStyles.main.textHeadingColor}`}>Add Expense Type</h2>
                        <span className={`material-symbols-outlined cursor-pointer ${themeStyles.main.textHeadingColor}`} onClick={() => setOpenAddTypeDialog(false)}>close</span>
                    </div>

                    <div className="mt-4 space-y-4">
                        <div>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Expense Type Name</label>
                            <input
                                type="text"
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={newExpenseTypeName}
                                onChange={(e) => setNewExpenseTypeName(e.target.value)}
                                required
                            />
                        </div>

                        <div>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Active</label>
                            <RadioGroup
                                row
                                value={isActiveType ? "Yes" : "No"}
                                onChange={(e) => setIsActiveType(e.target.value === "Yes")}
                            >
                                <FormControlLabel
                                    value="Yes"
                                    control={<Radio
                                        sx={{
                                            color: "gray",
                                            "&.Mui-checked": {
                                                color: "#167AC3",
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: 14,
                                            },
                                        }}
                                    />}
                                    label="Yes"
                                    sx={{ color: themeStyles.form.labelTextColor }}
                                />
                                <FormControlLabel
                                    value="No"
                                    control={<Radio
                                        sx={{
                                            color: "gray",
                                            "&.Mui-checked": {
                                                color: "#167AC3",
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: 14,
                                            },
                                        }}
                                    />}
                                    label="No"
                                    sx={{ color: themeStyles.form.labelTextColor }}
                                />
                            </RadioGroup>
                        </div>
                    </div>

                    <div className="mt-4 flex justify-end gap-4">
                        <Button
                            variant="contained"
                            sx={{ backgroundColor: colorConfigs.btn.main.bg }}
                            onClick={() => {
                                if (!newExpenseTypeName.trim()) return;
                                setPleaseWait(true);
                                axios.post(`${baseURL}vehicleExpenseType`, {
                                    expenseTypeName: newExpenseTypeName.trim(),
                                    isActive: isActiveType,
                                }, {
                                    headers: {
                                        'X-Tenant-ID': tenantId,
                                        "Content-Type": "application/json"
                                    },
                                    withCredentials: true
                                }).then(() => {
                                    setOpenAddTypeDialog(false);
                                    setNewExpenseTypeName('');
                                    setIsActiveType(true);
                                    fetchExpenseTypes();
                                    setPleaseWait(false);
                                    handleTenantError(null);
                                }).catch((error) => {
                                    handleTenantError(error);
                                    setPleaseWait(false);
                                });
                            }}
                        >
                            Save
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: colorConfigs.btn.cancel.bg,
                                border: colorConfigs.btn.cancel.border,
                                color: colorConfigs.btn.cancel.color
                            }}
                            onClick={() => setOpenAddTypeDialog(false)}
                        >
                            Cancel
                        </Button>

                    </div>
                </div>
            </Dialog>

            <CustomImagePopup
                showPopup={imagePopup}
                handlePopup={() => setImagePopup(false)}
                filePath={filePath}
                fileIndex={fileIndex}
                setFileIndex={setFileIndex}
                imageBaseUrlPath="commonFile"
            />

            <Dialog
                open={showWebcam}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '500px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[10px] py-[5px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>WebCam</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <Webcam
                                            width={500}
                                            height={320}
                                            ref={webRef}
                                            screenshotFormat="image/png"
                                            audio={false}
                                        />

                                        <div className="flex gap-4 mt-6">
                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.cancel.bg,
                                                    border: colorConfigs.btn.cancel.border,
                                                    color: colorConfigs.btn.cancel.color
                                                }}
                                                onClick={() => {

                                                    setShowWebcam(false)
                                                }}>
                                                Cancel
                                            </Button>

                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.main.bg
                                                }}
                                                onClick={() => {
                                                    if (webRef.current !== null) {
                                                        //@ts-ignore
                                                        const imageSrc = webRef.current.getScreenshot();

                                                        handleCapture(imageSrc, `${baseURL}reservationFile/upload`, tenantId, (imageFilePath: string) => {
                                                            if (imageFilePath) {
                                                                setFilePath((prevFileString: string[]) => [
                                                                    ...prevFileString,
                                                                    `${baseURL}reservationFile/Get?name=${imageFilePath}`
                                                                ]);

                                                                const newFile: any = {
                                                                    id: 0,
                                                                    fileName: `${baseURL}reservationFile/Get?name=${imageFilePath}`
                                                                };
                                                                setFilePath((prevFileString: any[]) => [
                                                                    ...prevFileString,
                                                                    newFile,
                                                                ]);
                                                            }
                                                        })
                                                        setShowWebcam(false);
                                                    }
                                                }}
                                            >
                                                Capture an Image
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            <PleaseWaitPopup pleaseWaitPopup={pleaseWait} />
        </>
    )
}